import { Dispatch, GetState } from "common/types";

import { getClient } from "services";

import {
  CrudConfig,
  makeCrudStateActionFactories
} from "seneca-common/utils/apiStates";

import {
  getFetchTagsError,
  getFetchTagsLastFetched,
  getFetchTagsStarted,
  getFetchTagsSuccess,
  receiveTagsAndCategories,
  tagsHaveFinishedFetching,
  tagsNeedFetching
} from "../state";
import { tagsCrudConfig } from "../state/consts";
import { makeCreateTag } from "./createTag";
import { makeDeleteTag } from "./deleteTag";
import { makeUpdateTag } from "./updateTag";

type FetchAllTagsOptions = {
  region?: string | null;
  limit?: number;
  includeNonCourseFilters?: boolean;
} | null;

const tagsStateActions = {
  receiveTagsAndCategories
};

const tagsStateSelectors = {
  getFetchTagsStarted,
  getFetchTagsSuccess,
  getFetchTagsError,
  getFetchTagsLastFetched,
  tagsHaveFinishedFetching,
  tagsNeedFetching
};

export function makeService(
  stateActions: any,
  stateSelectors: any,
  crudStateConfig: CrudConfig
) {
  const {
    fetchAllActionFactory,
    createActionFactory,
    updateActionFactory,
    deleteActionFactory
  } = makeCrudStateActionFactories(crudStateConfig);

  const { createTag, createCategory } = makeCreateTag(createActionFactory);
  const { updateTag, updateCategory } = makeUpdateTag(updateActionFactory);
  const { deleteTag, deleteCategory } = makeDeleteTag(deleteActionFactory);

  const fetchAllTagsAction = fetchAllActionFactory({
    requestFunction: options => getClient("tagService").fetchAll(options),
    storeAction: stateActions.receiveTagsAndCategories
  });

  const fetchAllTags = (options?: FetchAllTagsOptions) => {
    return async (dispatch: Dispatch, getState: GetState) => {
      return await dispatch(fetchAllTagsAction(options));
    };
  };

  const fetchAllTagsIfNotFetched = (options?: FetchAllTagsOptions) => {
    return (dispatch: Dispatch, getState: GetState) => {
      if (
        !stateSelectors.getFetchTagsStarted(getState()) &&
        !stateSelectors.getFetchTagsSuccess(getState())
      ) {
        return dispatch(fetchAllTags(options));
      }
    };
  };

  const fetchTagsForCourseRegionIfSet = (
    courseId: string,
    getCourseRegionSelector: (arg0: any, arg1: any) => string
  ) => {
    return async (dispatch: Dispatch, getState: GetState) => {
      const region = getCourseRegionSelector(getState(), courseId);

      if (region) {
        dispatch(
          fetchAllTags({
            region
          })
        );
      }
    };
  };

  const fetchTagsForRegion = (region: string | null) => {
    return async (dispatch: Dispatch) => {
      dispatch(
        fetchAllTags({
          region
        })
      );
    };
  };

  const makeCrudAndFetchAction = (crudAction: any) => {
    return (tagOrCategory: any, options?: FetchAllTagsOptions) =>
      async (dispatch: Dispatch, getState: GetState) => {
        await dispatch(crudAction(tagOrCategory));
        dispatch(fetchAllTagsAction(options));
      };
  };

  const createTagAndFetch = makeCrudAndFetchAction(createTag);
  const updateTagAndFetch = makeCrudAndFetchAction(updateTag);
  const deleteTagAndFetch = makeCrudAndFetchAction(deleteTag);
  const createCategoryAndFetch = makeCrudAndFetchAction(createCategory);
  const updateCategoryAndFetch = makeCrudAndFetchAction(updateCategory);
  const deleteCategoryAndFetch = makeCrudAndFetchAction(deleteCategory);

  return {
    fetchAllTagsAction,
    fetchAllTagsIfNotFetched,
    fetchAllTags,
    fetchTagsForCourseRegionIfSet,
    fetchTagsForRegion,
    //admin
    createTag,
    updateTag,
    deleteTag,
    createCategory,
    updateCategory,
    deleteCategory,
    createTagAndFetch,
    updateTagAndFetch,
    deleteTagAndFetch,
    createCategoryAndFetch,
    updateCategoryAndFetch,
    deleteCategoryAndFetch
  };
}

export const {
  fetchAllTagsAction,
  fetchAllTagsIfNotFetched,
  fetchAllTags,
  fetchTagsForCourseRegionIfSet,
  fetchTagsForRegion,
  //admin
  createTag,
  updateTag,
  deleteTag,
  createCategory,
  updateCategory,
  deleteCategory,
  createTagAndFetch,
  updateTagAndFetch,
  deleteTagAndFetch,
  createCategoryAndFetch,
  updateCategoryAndFetch,
  deleteCategoryAndFetch
} = makeService(tagsStateActions, tagsStateSelectors, tagsCrudConfig);
